import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/Navbar';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Save, ImageIcon, RotateCcw, Copy, Upload, X } from 'lucide-react';

const FIELDS = [
    // --- Site-level (root URL share preview) ---
    {
        key: 'siteHeadline',
        label: 'Site headline',
        hint: 'Big headline shown on the homepage share card. Appears when the root URL is shared.',
        multiline: false,
        group: 'site',
    },
    {
        key: 'siteSubheadline',
        label: 'Site subheadline / eyebrow',
        hint: 'Small line above the headline on the homepage share card.',
        multiline: false,
        group: 'site',
    },
    {
        key: 'siteDescription',
        label: 'Site description',
        hint: 'Paragraph shown on the homepage share card and in link previews on LinkedIn / X.',
        multiline: true,
        group: 'site',
    },
    {
        key: 'siteFooter',
        label: 'Site footer tagline',
        hint: 'Small line at the bottom of the homepage share card.',
        multiline: false,
        group: 'site',
    },
    // --- Per-recipient ---
    {
        key: 'ogEyebrow',
        label: 'OG image eyebrow',
        hint: 'Small line above the recipient\'s name on their share card.',
        multiline: false,
        group: 'recipient',
    },
    {
        key: 'ogFooter',
        label: 'OG image footer tagline',
        hint: 'Small line along the bottom of the recipient share card.',
        multiline: false,
        group: 'recipient',
    },
    {
        key: 'sharePageTitle',
        label: 'Share page title suffix',
        hint: 'Appended after the recipient\'s name in the shared page <title>.',
        multiline: false,
        group: 'recipient',
    },
    {
        key: 'descriptionTemplate',
        label: 'Description meta template',
        hint: 'Used as the OG:description meta on recipient shared links. Variables: {name}, {firstName}, {company}, {phases}, {milestones}.',
        multiline: true,
        group: 'recipient',
    },
    {
        key: 'linkedinCaption',
        label: 'LinkedIn caption (auto-copied on share)',
        hint: 'Copied to the visitor\'s clipboard when they tap LinkedIn share. Variables: {name}, {firstName}, {company}, {phases}, {milestones}.',
        multiline: true,
        group: 'recipient',
    },
    {
        key: 'twitterCaption',
        label: 'X (Twitter) caption',
        hint: 'Pre-filled into the X compose box. Variables: {name}, {firstName}, {company}, {phases}, {milestones}.',
        multiline: true,
        group: 'recipient',
    },
];

const DEMO_CTX = {
    name: 'Camille Laurent',
    firstName: 'Camille',
    company: 'Atelier Verre',
    phases: 'UnCloned® CEO, UnCloned® Publishing, UnCloned® Marketing & Launches +more',
    milestones: 20,
};

function fillTemplate(tpl, ctx) {
    if (!tpl) return '';
    let out = tpl;
    for (const [k, v] of Object.entries(ctx)) {
        out = out.split('{' + k + '}').join(String(v ?? ''));
    }
    return out;
}

export default function AdminShareSettings() {
    const [values, setValues] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [saving, setSaving] = useState(false);
    const [previewSlug, setPreviewSlug] = useState('');
    const [uploadingImg, setUploadingImg] = useState(false);
    const [uploadingFav, setUploadingFav] = useState(false);
    const fileInputRef = React.useRef(null);
    const faviconInputRef = React.useRef(null);

    useEffect(() => {
        api.get('/admin/share-settings').then(({ data }) => {
            setValues(data.settings);
            setDefaults(data.defaults);
        }).catch((e) => toast.error(formatApiError(e)));
        // pick first available recipient slug for the OG preview
        api.get('/recipients?sort=name').then(({ data }) => {
            if (data.length > 0) setPreviewSlug(data[0].slug);
        }).catch(() => {});
    }, []);

    const previewSrc = useMemo(() => {
        if (!previewSlug) return '';
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        // Cache-bust when the settings change (UTF-8 safe hash)
        let hash = 0;
        const src = values ? JSON.stringify(values) : '';
        for (let i = 0; i < src.length; i++) hash = (hash * 31 + src.charCodeAt(i)) | 0;
        return `${base}/api/og/${previewSlug}.png?v=${hash}`;
    }, [previewSlug, values]);

    const sitePreviewSrc = useMemo(() => {
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        let hash = 0;
        const src = values ? JSON.stringify(values) : '';
        for (let i = 0; i < src.length; i++) hash = (hash * 31 + src.charCodeAt(i)) | 0;
        return `${base}/api/og/site.png?v=${hash}`;
    }, [values]);

    const faviconPreviewSrc = useMemo(() => {
        if (!values?.faviconOverride) return '';
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        let hash = 0;
        for (let i = 0; i < values.faviconOverride.length; i++) hash = (hash * 31 + values.faviconOverride.charCodeAt(i)) | 0;
        return `${base}/api/favicon.ico?v=${hash}`;
    }, [values]);

    const shareUrlDemo = useMemo(() => {
        if (!previewSlug) return '';
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        return `${base}/api/share/${previewSlug}`;
    }, [previewSlug]);

    if (!values) {
        return (
            <div className="min-h-screen bg-black">
                <Navbar variant="admin" />
                <div className="max-w-5xl mx-auto px-6 py-24 flex items-center justify-center text-white/60">
                    <Loader2 className="animate-spin" size={20} />
                    <span className="ml-3 uppercase tracking-[0.2em] text-xs">Loading</span>
                </div>
            </div>
        );
    }

    const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.patch('/admin/share-settings', values);
            setValues(data.settings);
            toast.success('Share settings saved — next post will use the new copy');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const restoreDefault = (k) => {
        if (!defaults) return;
        set(k, defaults[k]);
    };

    const copyDemoUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrlDemo);
            toast.success('Preview share URL copied — paste into LinkedIn Post Inspector');
        } catch {
            toast.error('Could not copy');
        }
    };

    const uploadCustomSiteImage = async (file) => {
        if (!file) return;
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/admin/share-settings/site-image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setValues(data.settings);
            toast.success('Custom site image uploaded — this will now show on all shares');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingImg(false);
        }
    };

    const clearCustomSiteImage = async () => {
        setUploadingImg(true);
        try {
            const { data } = await api.delete('/admin/share-settings/site-image');
            setValues(data.settings);
            toast.success('Reverted to the auto-generated site image');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingImg(false);
        }
    };

    const uploadFavicon = async (file) => {
        if (!file) return;
        setUploadingFav(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/admin/share-settings/favicon', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setValues(data.settings);
            toast.success('Favicon uploaded — hard-refresh (Ctrl+Shift+R / Cmd+Shift+R) to see it');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingFav(false);
        }
    };

    const clearFavicon = async () => {
        setUploadingFav(true);
        try {
            const { data } = await api.delete('/admin/share-settings/favicon');
            setValues(data.settings);
            toast.success('Favicon removed');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingFav(false);
        }
    };

    return (
        <div className="min-h-screen bg-black">
            <Navbar variant="admin" />

            <form
                onSubmit={onSave}
                data-testid="share-settings-form"
                className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14"
            >
                <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-[0.28em] text-un-lime font-semibold">Share preview</div>
                        <h1 className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl">
                            Share settings
                        </h1>
                        <p className="mt-2 text-white/60 font-body max-w-2xl">
                            Edit the copy that appears on the LinkedIn / X preview cards and the pre-filled captions.
                            Changes take effect immediately for every recipient — no redeploy needed.
                        </p>
                    </div>
                    <button
                        data-testid="share-settings-save-btn"
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3 hover:bg-white transition-colors disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    {/* Editable fields */}
                    <div className="lg:col-span-3 space-y-8">
                        {/* Site-level */}
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-un-water font-semibold mb-3">
                                Site preview (shown when the homepage URL is shared)
                            </div>

                            {/* Custom site image upload */}
                            <div
                                data-testid="site-image-upload-panel"
                                className="rounded-2xl border border-white/10 bg-neutral-950 p-5 mb-5"
                            >
                                <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold">
                                    Custom site share image
                                </div>
                                <p className="text-[11px] text-white/50 mt-1 mb-4">
                                    Upload your own designed image (recommended 1200×630 or 1200×600, PNG or JPG). Overrides the auto-generated card. Clear it anytime to switch back.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    data-testid="site-image-input"
                                    onChange={(e) => uploadCustomSiteImage(e.target.files?.[0])}
                                />
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingImg}
                                        data-testid="site-image-upload-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-un-lime transition-colors disabled:opacity-60"
                                    >
                                        {uploadingImg ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                        {uploadingImg ? 'Uploading' : (values.siteImageOverride ? 'Replace image' : 'Upload custom image')}
                                    </button>
                                    {values.siteImageOverride && (
                                        <button
                                            type="button"
                                            onClick={clearCustomSiteImage}
                                            disabled={uploadingImg}
                                            data-testid="site-image-clear-btn"
                                            className="inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 text-sm px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                                        >
                                            <X size={14} /> Use auto-generated
                                        </button>
                                    )}
                                </div>
                                {values.siteImageOverride && (
                                    <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-un-lime">
                                        Custom image active
                                    </div>
                                )}
                            </div>

                            {/* Favicon upload */}
                            <div
                                data-testid="favicon-upload-panel"
                                className="rounded-2xl border border-white/10 bg-neutral-950 p-5 mb-5"
                            >
                                <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold">
                                    Site favicon
                                </div>
                                <p className="text-[11px] text-white/50 mt-1 mb-4">
                                    Upload a favicon (the little icon in the browser tab). Square PNG (32×32 or 64×64), SVG, or .ico. Browsers cache aggressively — a hard refresh may be needed to see updates.
                                </p>
                                <input
                                    ref={faviconInputRef}
                                    type="file"
                                    accept=".ico,image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,image/jpeg,image/webp"
                                    hidden
                                    data-testid="favicon-input"
                                    onChange={(e) => uploadFavicon(e.target.files?.[0])}
                                />
                                <div className="flex items-center gap-3 flex-wrap">
                                    {values.faviconOverride && (
                                        <div className="h-10 w-10 rounded-md border border-white/15 bg-black flex items-center justify-center overflow-hidden">
                                            <img
                                                data-testid="favicon-current"
                                                src={faviconPreviewSrc}
                                                alt="favicon"
                                                className="h-8 w-8 object-contain"
                                            />
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => faviconInputRef.current?.click()}
                                        disabled={uploadingFav}
                                        data-testid="favicon-upload-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-un-lime transition-colors disabled:opacity-60"
                                    >
                                        {uploadingFav ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                        {uploadingFav ? 'Uploading' : (values.faviconOverride ? 'Replace favicon' : 'Upload favicon')}
                                    </button>
                                    {values.faviconOverride && (
                                        <button
                                            type="button"
                                            onClick={clearFavicon}
                                            disabled={uploadingFav}
                                            data-testid="favicon-clear-btn"
                                            className="inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 text-sm px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                                        >
                                            <X size={14} /> Remove
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-5">
                                {FIELDS.filter((f) => f.group === 'site').map((f) => renderField(f, values, set, restoreDefault))}
                            </div>
                        </div>

                        {/* Per-recipient */}
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-un-lime font-semibold mb-3">
                                Recipient preview (shown when a recipient&apos;s URL is shared)
                            </div>
                            <div className="space-y-5">
                                {FIELDS.filter((f) => f.group === 'recipient').map((f) => renderField(f, values, set, restoreDefault))}
                            </div>
                        </div>

                        {/* Nav CTA button */}
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-un-lime font-semibold mb-3">
                                Nav bar Call-To-Action button
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-neutral-950 p-5 space-y-5">
                                <label className="flex items-start gap-3 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        data-testid="nav-cta-enabled"
                                        checked={!!values.navCtaEnabled}
                                        onChange={(e) => set('navCtaEnabled', e.target.checked)}
                                        className="mt-1 h-4 w-4 accent-un-lime"
                                    />
                                    <span>
                                        <span className="block text-sm text-white font-semibold">
                                            Show the CTA button in the nav
                                        </span>
                                        <span className="block text-xs text-white/50 mt-1">
                                            When off, the lime call-to-action button disappears from the top nav on every page, on both desktop and mobile.
                                        </span>
                                    </span>
                                </label>

                                <div>
                                    <label className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">
                                        Button label
                                    </label>
                                    <input
                                        type="text"
                                        data-testid="nav-cta-label"
                                        value={values.navCtaLabel || ''}
                                        onChange={(e) => set('navCtaLabel', e.target.value)}
                                        placeholder="Join UnCloned® Community"
                                        className="w-full rounded-2xl bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">
                                        Button link (opens in a new tab)
                                    </label>
                                    <input
                                        type="url"
                                        data-testid="nav-cta-url"
                                        value={values.navCtaUrl || ''}
                                        onChange={(e) => set('navCtaUrl', e.target.value)}
                                        placeholder="https://www.skool.com/uncloned-8397"
                                        className="w-full rounded-2xl bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Live previews */}
                    <div className="lg:col-span-2">
                        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-5 sticky top-24 space-y-6">
                            {/* Site OG */}
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold flex items-center gap-2">
                                    <ImageIcon size={14} /> Site share preview
                                </div>
                                <p className="text-[11px] text-white/50 mt-1 mb-3">
                                    The image LinkedIn / X will fetch when the homepage URL is shared.
                                </p>
                                <div className="rounded-xl overflow-hidden bg-black border border-white/10">
                                    <img
                                        data-testid="share-site-preview"
                                        src={sitePreviewSrc}
                                        alt="Site OG preview"
                                        className="w-full h-auto"
                                    />
                                </div>
                            </div>

                            {/* Recipient OG */}
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold flex items-center gap-2">
                                    <ImageIcon size={14} /> Recipient share preview
                                </div>
                                <p className="text-[11px] text-white/50 mt-1 mb-3">
                                    Example using the first recipient in your directory.
                                </p>
                                {previewSrc ? (
                                    <div className="rounded-xl overflow-hidden bg-black border border-white/10">
                                        <img
                                            data-testid="share-og-preview"
                                            src={previewSrc}
                                            alt="Recipient OG preview"
                                            className="w-full h-auto"
                                        />
                                    </div>
                                ) : (
                                    <div className="rounded-xl bg-black border border-dashed border-white/15 p-8 text-center text-white/40 text-xs">
                                        Add a recipient to see a preview
                                    </div>
                                )}
                            </div>

                            {shareUrlDemo && (
                                <button
                                    type="button"
                                    onClick={copyDemoUrl}
                                    data-testid="share-copy-preview-url"
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white text-xs font-semibold py-2.5 hover:border-un-lime hover:text-un-lime transition-colors"
                                >
                                    <Copy size={12} /> Copy recipient share URL for testing
                                </button>
                            )}
                            <a
                                href="https://www.linkedin.com/post-inspector/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="block text-center text-[11px] text-white/50 hover:text-un-water underline underline-offset-2"
                            >
                                Open LinkedIn Post Inspector →
                            </a>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

function renderField(f, values, set, restoreDefault) {
    return (
        <div
            key={f.key}
            data-testid={`share-field-${f.key}`}
            className="rounded-2xl border border-white/10 bg-neutral-950 p-5"
        >
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold">{f.label}</div>
                    <div className="text-[11px] text-white/50 mt-1">{f.hint}</div>
                </div>
                <button
                    type="button"
                    title="Restore default"
                    onClick={() => restoreDefault(f.key)}
                    data-testid={`share-restore-${f.key}`}
                    className="text-white/50 hover:text-un-lime text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1"
                >
                    <RotateCcw size={12} /> default
                </button>
            </div>
            {f.multiline ? (
                <textarea
                    data-testid={`share-input-${f.key}`}
                    value={values[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    rows={3}
                    className="w-full rounded-xl bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry font-body"
                />
            ) : (
                <input
                    data-testid={`share-input-${f.key}`}
                    type="text"
                    value={values[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="w-full rounded-full bg-black border border-white/15 px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                />
            )}
            {f.multiline && (
                <div className="mt-3 rounded-lg bg-black/60 border border-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                        Preview using demo data (Camille Laurent)
                    </div>
                    <div className="text-sm text-white/80 whitespace-pre-wrap font-body">
                        {fillTemplate(values[f.key], DEMO_CTX) || <span className="text-white/30">— empty —</span>}
                    </div>
                </div>
            )}
        </div>
    );
}
